import { enableAllPlugins, produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import type { AsyncActionHandlers } from 'use-reducer-async';
import type { Reducer } from 'react';
import omit from 'lodash/omit';
import FileRequestAPI from '../../../../api/FileRequest';
import ItemAPI from '../../../../api/Item';

import readFileAsDataURL from '../../../../common/utils/readFileAsDataURL';
import {
  ACCEPTED_IMG_TYPES,
  FILE_REQUEST_STATUS,
  MAX_CUSTOM_LOGO_SIZE,
  MAX_NUMBER_OF_FIELDS,
  MODE,
} from '../../../../constants';

import builderNotificationMessages from '../../../new-file-request-builder/builder-notification/messages';
import {
  addCheckboxField,
  addDescriptionField,
  addEmailField,
  addMetadataField,
  addRadioField,
  addUploadField,
  removeField,
  toggleFieldRequired,
} from '../../../new-file-request-builder/FormElementMaker';
import {
  getFormElementKey,
  getLayoutAfterFieldColToggle,
  isItemInTwoColumn,
  TOTAL_BORDER_WIDTH,
  updateTwoColItemHeightInLayout,
} from '../../../new-file-request-builder/grid-layout-utils';
import {
  ACTION_ADD_CHECKBOX_FIELD,
  ACTION_ADD_DESCRIPTION_FIELD,
  ACTION_ADD_EMAIL_FIELD,
  ACTION_ADD_FIELD,
  ACTION_ADD_METADATA_FIELD,
  ACTION_ADD_RADIO_FIELD,
  ACTION_ADD_UPLOAD_FIELD,
  ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD,
  ACTION_CLEAR_METADATA_TEMPLATE,
  ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL,
  ACTION_CLOSE_METADATA_PICKER,
  ACTION_CLOSE_SETTINGS_PANEL,
  ACTION_CLOSE_SHARE_MODAL,
  ACTION_CLOSE_UPLOAD_LOGO_MODAL,
  ACTION_COPY_FILE_REQUEST,
  ACTION_COPY_FILE_REQUEST_IN_PROGRESS,
  ACTION_DEACTIVATE_BAD_FILE_REQUEST,
  ACTION_DELETE_FILE_REQUEST,
  ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL,
  ACTION_GET_FOLDER_TREE,
  ACTION_GET_FOLDER_TREE_IN_PROGRESS,
  ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
  ACTION_ON_HELP,
  ACTION_ON_UNFOCUS_ELEMENT,
  ACTION_OPEN_FIELD_SELECTOR_DRAWER,
  ACTION_OPEN_METADATA_PICKER,
  ACTION_OPEN_SHARE_MODAL,
  ACTION_REMOVE_DESCRIPTION,
  ACTION_REMOVE_FIELD,
  ACTION_REORDER_GROUP_FIELDS,
  ACTION_SEARCH_ITEMS,
  ACTION_SEARCH_ITEMS_IN_PROGRESS,
  ACTION_SELECT_METADATA,
  ACTION_SELECT_METADATA_TEMPLATE,
  ACTION_SET_BUILDER_ERROR,
  ACTION_SET_DESCRIPTION,
  ACTION_SET_DESCRIPTION_EDITABLE,
  ACTION_SET_FORM_ELEMENT_TESTIDS,
  ACTION_SET_LABEL,
  ACTION_SET_LIST_OPTIONS_ERROR,
  ACTION_SET_UPLOADED_LOGO,
  ACTION_STORE_FORM_ELEMENT_HEIGHT,
  ACTION_TOGGLE_FIELD_COLUMN,
  ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
  ACTION_TOGGLE_REQUIRED_FIELD,
  ACTION_UPDATE_GROUP_LAYOUT,
  ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT,
  ACTION_UPDATE_LIST_OPTIONS,
  ACTION_UPDATE_MAXIMUM_SELECTIONS,
  ACTION_UPDATE_UPLOAD_LOGO_ITEM,
  ACTION_UPLOAD_LOGO,
  ACTION_UPLOAD_LOGO_EMPTY,
  ACTION_UPLOAD_LOGO_ERROR,
  ACTION_UPLOAD_LOGO_IN_PROGRESS,
  ACTION_UPLOAD_LOGO_IS_NOT_IMAGE,
  ACTION_UPLOAD_LOGO_TOO_LARGE,
} from '../builder-constant';
import selector from '../builder-selector';
import type {
  BuilderAction,
  BaseAction,
  onDeleteType,
  State,
} from '../builder-type';
import { calculateNewMaxSelections, willExceedMaxFields } from '../utils';
import {
  actionSave,
  actionToggleFileRequestActivity,
  recordBuilderHeaderAnalytics,
  removeFieldFromFormContent,
} from './builder-action.utils';
import { CREATE_SAVE_ACTIONS } from './create-save-action/create-save-action-type';

// Enable all immer plugins
enableAllPlugins();

type BuilderActionArgs = {
  onDelete: onDeleteType;
};

export default ({
  onDelete,
}: BuilderActionArgs): AsyncActionHandlers<
  Reducer<State, BuilderAction>,
  BaseAction
> => {
  return {
    [ACTION_OPEN_METADATA_PICKER]: (dispatch, getState) => async action => {
      const state = getState();

      if (willExceedMaxFields(state)) {
        dispatch({
          type: ACTION_SET_BUILDER_ERROR,
          errorMessage: {
            ...builderNotificationMessages.errorMaxFieldsNotification,
            values: { maxCount: MAX_NUMBER_OF_FIELDS },
          },
        });
        return;
      }

      dispatch(action);
    },
    [ACTION_CLOSE_METADATA_PICKER]: dispatch => async action => {
      dispatch(action);
    },
    // Figure out where this is being used? I don't think this is being used anymore
    // ACTION_INSERT_METADATA is used instead and this should be deprecated
    [ACTION_SELECT_METADATA_TEMPLATE]: (dispatch, getState) => async action => {
      const { newMetadataTemplate } = action;
      const state = getState();
      let formContent = selector.getFormContent(state);
      forEach(
        filter(newMetadataTemplate?.fields, field => field.isSelected === true),
        metadataField => {
          formContent = addMetadataField(
            metadataField,
            newMetadataTemplate?.id,
            formContent,
          );
        },
      );
      dispatch({ ...action, formContent });
    },
    [ACTION_CLEAR_METADATA_TEMPLATE]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      let formContent = selector.getFormContent(state);
      forEach(
        filter(formContent.components, component =>
          has(component, 'metadataTemplateId'),
        ),
        component => {
          formContent = removeField(component.id, formContent);
        },
      );
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'cleartemplate',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ ...action, formContent });
    },
    [ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_OPEN_FIELD_SELECTOR_DRAWER]:
      (dispatch, getState) => async action => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addfield',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch(action);
      },

    [ACTION_SELECT_METADATA]: (dispatch, getState) => async () => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addmetadata',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
    },

    [ACTION_ADD_METADATA_FIELD]: (dispatch, getState) => async action => {
      const state = getState();
      const { metadataField, metadataTemplateId } = action;
      let formContent = selector.getFormContent(state);
      formContent = addMetadataField(
        metadataField,
        metadataTemplateId,
        formContent,
      );
      const newMetadataTemplate = produce(
        selector.getActiveMetadataTemplate(state),
        newTemplate => {
          const field = find(get(newTemplate, 'fields', []), [
            'key',
            metadataField?.key,
          ]);
          if (field) field.isSelected = true;
        },
      );
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action, newMetadataTemplate });
    },
    [ACTION_ADD_EMAIL_FIELD]: (dispatch, getState) => async action => {
      const { intl } = action;
      const state = getState();
      let formContent = selector.getFormContent(state);
      const formType = get(state, 'workingFileRequest.form.formType');
      formContent = addEmailField(formContent, intl, formType);
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addemail',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action });
    },
    [ACTION_ADD_DESCRIPTION_FIELD]: (dispatch, getState) => async action => {
      const { intl } = action;
      const state = getState();
      let formContent = selector.getFormContent(state);
      formContent = addDescriptionField(formContent, intl);
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addfiledescription',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action });
    },

    [ACTION_ADD_RADIO_FIELD]: (dispatch, getState) => async action => {
      const state = getState();
      let formContent = selector.getFormContent(state);
      formContent = addRadioField(formContent);
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addradio',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action });
    },
    [ACTION_ADD_CHECKBOX_FIELD]: (dispatch, getState) => async action => {
      const state = getState();
      let formContent = selector.getFormContent(state);
      formContent = addCheckboxField(formContent);
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addcheckbox',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action });
    },
    [ACTION_ADD_UPLOAD_FIELD]: (dispatch, getState) => async action => {
      const { intl, mode } = action;
      const state = getState();
      let formContent = selector.getFormContent(state);
      formContent = addUploadField(formContent, intl, mode);
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'adduploadzone',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ type: ACTION_ADD_FIELD, formContent });
      dispatch({ ...action });
    },
    [ACTION_REMOVE_FIELD]: (dispatch, getState) => async action => {
      const { id, intl, multipleMetadataTemplatesEnabled } = action;
      if (!id || !intl) {
        return;
      }

      const state = getState();

      // If metadata field, unselect field
      let newMetadataTemplates = cloneDeep(
        selector.getActiveMetadataTemplates(state),
      );

      const component = selector.getComponentForId(state, id);
      if (!component) {
        return;
      }

      if (
        newMetadataTemplates?.length > 0 &&
        has(component, 'metadataFieldKey') &&
        has(component, 'metadataTemplateId')
      ) {
        // find the template for field to remove and set isSelected false for the fields
        const templateToUpdate = newMetadataTemplates.find(
          tempalte => tempalte.id === component.metadataTemplateId,
        );

        if (templateToUpdate) {
          const field = find(templateToUpdate.fields, [
            'key',
            component.metadataFieldKey,
          ]);
          if (field) field.isSelected = false;
          if (templateToUpdate.fields.every(({ isSelected }) => !isSelected)) {
            // remove template if all the fields in this template is unselected
            newMetadataTemplates = newMetadataTemplates.filter(
              templateEl => templateEl.id !== templateToUpdate.id,
            );
          }
        }
      }
      // Update testids
      const formElementTestids = omit(state.formElementTestids, [id]);

      const formContent = removeFieldFromFormContent(state, component, intl);

      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `remove`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({
        type: ACTION_REMOVE_FIELD,
        formContent,
        newMetadataTemplates,
        multipleMetadataTemplatesEnabled,
      });
      dispatch({ type: ACTION_SET_FORM_ELEMENT_TESTIDS, formElementTestids });
    },
    [ACTION_TOGGLE_REQUIRED_FIELD]: (dispatch, getState) => async action => {
      const state = getState();
      const { id } = action;
      let formContent = selector.getFormContent(state);
      formContent = toggleFieldRequired(id, formContent);
      const newRequired = get(
        selector.getComponentForId(state, id),
        'required',
      );
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `markasrequired|${newRequired ? 'check' : 'uncheck'}`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ ...action, formContent });
    },
    [ACTION_DELETE_FILE_REQUEST]: (dispatch, getState) => async action => {
      const { exitBuilder, navigation, baseUrl, mode } = action;
      const state = getState();
      const { workingFileRequest, resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'delete',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      FileRequestAPI.deleteFileRequest(workingFileRequest).then(() => {
        dispatch({ type: ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL });
        if (typeof onDelete === 'function') {
          onDelete();
        }

        exitBuilder(baseUrl, mode, state, navigation.applicationHistory);
      });
    },
    [ACTION_ON_HELP]: (dispatch, getState) => async () => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'gethelp',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
    },
    [ACTION_ON_UNFOCUS_ELEMENT]: dispatch => async action => {
      dispatch(action);
      dispatch({ type: ACTION_CLOSE_SETTINGS_PANEL });
    },
    [ACTION_DEACTIVATE_BAD_FILE_REQUEST]: (dispatch, getState) => async () => {
      const state = getState();
      if (state.savedFileRequest.status === FILE_REQUEST_STATUS.ACTIVE) {
        await actionToggleFileRequestActivity(dispatch, state);
        dispatch({ type: ACTION_NOTIFY_FILE_REQUEST_INACTIVE });
      }
      dispatch({ type: ACTION_DEACTIVATE_BAD_FILE_REQUEST });
    },
    [ACTION_TOGGLE_FILE_REQUEST_ACTIVITY]: (dispatch, getState) => async () => {
      const state = getState();
      await actionToggleFileRequestActivity(dispatch, state);
    },

    [ACTION_REORDER_GROUP_FIELDS]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      if (resin) {
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: 'fieldreorder, addnewfeild',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
      }
      dispatch(action);
    },

    [ACTION_OPEN_SHARE_MODAL]: (dispatch, getState) => async action => {
      const state = getState();
      const { workingFileRequest } = state;
      const formType = get(workingFileRequest, 'type');
      const hasUnsavedChanges = selector.hasUnsavedChanges(state);
      recordBuilderHeaderAnalytics(state, 'share');
      // Save if needed before sharing
      if (hasUnsavedChanges || formType === MODE.FILE_REQUEST) {
        dispatch({ type: CREATE_SAVE_ACTIONS.SAVE_BEFORE_SHARE_IN_PROGRESS });
        const saveSuccessful = await actionSave(dispatch, state)();

        if (saveSuccessful) {
          dispatch(action);
        }
        // If save fails, we don't proceed with the share modal
      } else {
        dispatch(action);
      }
    },

    [ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL]:
      dispatch => async action => {
        dispatch(action);
      },

    [ACTION_CLOSE_SHARE_MODAL]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_SET_LABEL]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_UPDATE_LIST_OPTIONS]: dispatch => async action => {
      dispatch(action);
    },
    [ACTION_UPDATE_MAXIMUM_SELECTIONS]:
      (dispatch, getState) => async action => {
        const { id, value } = action;
        const state = getState();
        const numOptions = selector.getOptions(state, id).length;
        const newMaxSelections = calculateNewMaxSelections(value, numOptions);
        dispatch({ ...action, value: newMaxSelections });
      },

    [ACTION_SET_LIST_OPTIONS_ERROR]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_SET_DESCRIPTION]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_SET_DESCRIPTION_EDITABLE]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `adddescription|check`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch(action);
    },

    [ACTION_REMOVE_DESCRIPTION]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `adddescription|uncheck`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch(action);
    },
    [ACTION_SEARCH_ITEMS]: dispatch => async action => {
      dispatch({ type: ACTION_SEARCH_ITEMS_IN_PROGRESS });
      const { query, isStartingSearch } = action;
      ItemAPI.searchItems(query, isStartingSearch).then(data => {
        dispatch({ ...action, data });
      });
    },

    [ACTION_GET_FOLDER_TREE]: dispatch => async action => {
      dispatch({ type: ACTION_GET_FOLDER_TREE_IN_PROGRESS });
      const { folderId, pageNumber } = action;
      ItemAPI.getFolderTree(folderId, pageNumber).then(data => {
        dispatch({ ...action, data });
      });
    },
    [ACTION_COPY_FILE_REQUEST]: (dispatch, getState) => async action => {
      const state = getState();
      const { workingFileRequest } = state;
      dispatch({ type: ACTION_COPY_FILE_REQUEST_IN_PROGRESS });
      const { selectionItems } = action;
      FileRequestAPI.copyFileRequest(
        workingFileRequest,

        selectionItems?.map(selectionItem => selectionItem.id),
      ).then(data => {
        dispatch({ ...action, data });
      });
    },
    [ACTION_UPDATE_UPLOAD_LOGO_ITEM]: dispatch => async action => {
      const { file } = action;
      if (!file) {
        dispatch({ ...action, dataUrl: null });
        return;
      }

      const { size, type } = file;
      /**
       * Dispatch error if file is larger than 1MB
       * Dispatch error if file is not png or jpg
       */
      if (!ACCEPTED_IMG_TYPES.includes(type)) {
        dispatch({ type: ACTION_UPLOAD_LOGO_IS_NOT_IMAGE });
        return;
      }
      if (size > MAX_CUSTOM_LOGO_SIZE) {
        dispatch({ type: ACTION_UPLOAD_LOGO_TOO_LARGE });
        return;
      }

      const dataUrl = await readFileAsDataURL(file);
      dispatch({ ...action, dataUrl, file });
    },

    [ACTION_UPLOAD_LOGO]: (dispatch, getState) => async action => {
      const state = getState();
      const {
        workingFileRequest,
        uploadLogoModalState: { dataUrl, file },
        themePanelState: { stagedLogoPath },
      } = state;
      if (!file) {
        dispatch({ type: ACTION_UPLOAD_LOGO_EMPTY });
        return;
      }

      const isStagedLogo =
        stagedLogoPath !==
        get(state, 'savedFileRequest.form.content.theme.logo.key');

      const previousImagePath = isStagedLogo ? stagedLogoPath : '';

      dispatch({ type: ACTION_UPLOAD_LOGO_IN_PROGRESS });

      try {
        const { picPath } = await FileRequestAPI.uploadFileRequestLogo(
          workingFileRequest,
          file,
          previousImagePath,
        );
        dispatch({ ...action, dataUrl, path: picPath });

        dispatch({ type: ACTION_CLOSE_UPLOAD_LOGO_MODAL });
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      } catch (e: any) {
        dispatch({ type: ACTION_UPLOAD_LOGO_ERROR });
      }
    },

    [ACTION_SET_UPLOADED_LOGO]: (dispatch, getState) => async action => {
      const state = getState();
      const {
        uploadLogoModalState: { dataUrl, file },
      } = state;

      if (!file || !dataUrl) {
        dispatch({ type: ACTION_UPLOAD_LOGO_EMPTY });
        return;
      }
      dispatch({ ...action, dataUrl, path: file.name, file });
      dispatch({ type: ACTION_CLOSE_UPLOAD_LOGO_MODAL });
    },
    [ACTION_TOGGLE_FIELD_COLUMN]: (dispatch, getState) => async action => {
      const { id, groupId } = action;
      const state = getState();

      const pageInfo = selector.getPageInfoForGroupId(state, groupId);
      if (!pageInfo) {
        return;
      }

      const { layout: formLayout } = pageInfo;
      if (!formLayout) {
        return;
      }

      const newLayout = getLayoutAfterFieldColToggle(formLayout, id);
      dispatch({
        type: ACTION_UPDATE_GROUP_LAYOUT,
        layout: newLayout,
        id: groupId,
      });
    },
    [ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT]:
      (dispatch, getState) => async action => {
        const {
          id,
          groupId,
          value: { elementHeight },
        } = action;
        const state = getState();
        const prevElementHeight = get(
          state,
          `formElementsHeightState.${getFormElementKey(groupId, id)}`,
        );

        const pageInfo = selector.getPageInfoForGroupId(state, groupId);
        if (!pageInfo) {
          return;
        }

        const { layout } = pageInfo;
        const newLayout = cloneDeep(layout);
        if (!newLayout) {
          return;
        }

        const itemIndex = newLayout.findIndex(it => it.i === id);
        const itemOnUpdate = newLayout[itemIndex];
        if (
          prevElementHeight === undefined ||
          prevElementHeight !== elementHeight
        ) {
          dispatch({
            type: ACTION_STORE_FORM_ELEMENT_HEIGHT,
            groupId,
            id,
            value: { elementHeight },
          });
        }
        // ignore height change due to on focus border display
        if (
          prevElementHeight !== undefined &&
          Math.abs(elementHeight - prevElementHeight) > TOTAL_BORDER_WIDTH
        ) {
          if (isItemInTwoColumn(itemOnUpdate)) {
            updateTwoColItemHeightInLayout(
              newLayout,
              itemIndex,
              prevElementHeight,
              elementHeight,
              groupId,
              get(state, `formElementsHeightState`, {}),
            );
          }
          dispatch({
            type: ACTION_UPDATE_GROUP_LAYOUT,
            layout: newLayout,
            id: groupId,
          });
        }
      },

    [ACTION_UPDATE_GROUP_LAYOUT]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      if (resin) {
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: 'draganddrop',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
      }
      dispatch(action);
    },
  };
};
